import { getCountries, getCountryCallingCode } from 'libphonenumber-js';

export interface PhoneCountryOption {
  value: string;
  label: string;
}

/**
 * Häufigste Länder, die oben in der Liste angezeigt werden sollen
 */
const PRIORITY_COUNTRIES = ['DE', 'AT', 'CH', 'US', 'GB', 'FR'];

/**
 * Generiert eine Liste von Telefonvorwahlen-Optionen aus libphonenumber-js
 * @param locale Optional: Locale für Ländernamen (z.B. 'de', 'en'). Standard: 'en'
 * @returns Array von Optionen mit value (Vorwahl) und label (Format: "+XX (Country Name)")
 */
export function getPhoneCountryOptions(locale: string = 'en'): PhoneCountryOption[] {
  const countryCodes = getCountries();
  const priorityOptions: PhoneCountryOption[] = [];
  const otherOptions: PhoneCountryOption[] = [];

  // Normalisiere Locale (z.B. 'de-DE' -> 'de')
  const normalizedLocale = locale.split('-')[0].toLowerCase();

  // Verwende Intl.DisplayNames für Ländernamen (wie im Rest des Codebases)
  const regionNames = new Intl.DisplayNames([normalizedLocale === 'de' ? 'de' : 'en'], { type: 'region' });

  // Generiere Optionen für alle Länder
  for (const countryCode of countryCodes) {
    try {
      const callingCode = getCountryCallingCode(countryCode as any);
      // Verwende Intl.DisplayNames für Ländernamen
      const countryName = regionNames.of(countryCode) || countryCode;

      const option: PhoneCountryOption = {
        value: `+${callingCode}`,
        label: `+${callingCode} (${countryName})`,
      };

      // Trenne zwischen Prioritäts- und anderen Ländern
      if (PRIORITY_COUNTRIES.includes(countryCode)) {
        priorityOptions.push(option);
      } else {
        otherOptions.push(option);
      }
    } catch (error) {
      // Überspringe Länder, für die keine Vorwahl verfügbar ist
      console.warn(`Could not get calling code for country ${countryCode}:`, error);
    }
  }

  // Sortiere Prioritätsländer in der definierten Reihenfolge
  const sortedPriorityOptions = PRIORITY_COUNTRIES.map(code =>
    priorityOptions.find(opt => {
      try {
        const callingCode = getCountryCallingCode(code as any);
        return opt.value === `+${callingCode}`;
      } catch {
        return false;
      }
    }),
  ).filter((opt): opt is PhoneCountryOption => opt !== undefined);

  // Sortiere andere Länder alphabetisch nach Ländername
  otherOptions.sort((a, b) => {
    // Extrahiere Ländername aus Label (Format: "+XX (Country Name)")
    const nameA = a.label.match(/\((.+)\)/)?.[1] || '';
    const nameB = b.label.match(/\((.+)\)/)?.[1] || '';
    return nameA.localeCompare(nameB, normalizedLocale);
  });

  // Kombiniere: Prioritätsländer zuerst, dann alle anderen
  return [...sortedPriorityOptions, ...otherOptions];
}
